<template>
  <div v-if="amendedEntries.length > 0" id="image-carousel" class="slider max-w-full rounded-xl overflow-hidden">
    <vue-glide ref="slider" v-model="activeSlide" type="slider" class="max-w-full" :per-view="1">
      <vue-glide-slide v-for="(entry, index) in amendedEntries" :key="index">
        <div :class="{ 'pb-6': addShadow }">
          <div :class="{ shadowed: addShadow }">
            <picture v-if="entry.image && entry.image.img">
              <source v-for="(source, key) in entry.image.sources" :key="key" :media="source.media" :width="source.width" :height="source.height" :srcset="source.src" />
              <img
                :width="entry.image.img.width"
                :height="entry.image.img.height"
                :src="entry.image.img.src"
                :alt="entry.image.source || entry.image.title"
                class="w-full h-full rounded-xl"
              />
            </picture>

            <div v-else-if="get(entry, 'youtube.code')" class="aspect-video youtube rounded-xl overflow-hidden">
              <div class="w-full h-full" v-html="entry.youtube.code" />
            </div>

            <div v-if="entry.subtitle" class="mb-4 pb-4 pt-3 px-4 font-bold border-b border-white md:px-12">
              {{ entry.subtitle }}
            </div>

            <div v-if="entry.introduction" class="pl-12 pr-2 pt-3 md:pr-0" v-html="entry.introduction" />
          </div>
        </div>
      </vue-glide-slide>
    </vue-glide>

    <div v-if="amendedEntries.length > 1" class="px-6 md:px-0">
      <button
        class="border-box float-left mt-4 w-2/5 cursor-pointer focus:ring-1 focus:ring-white focus:ring-offset-1 focus:rounded-md hover:text-app-green text-black transform transition duration-200"
        :aria-label="t_('prev')"
        @click="slider.glide.go('<')"
      >
        <AtomIconArrowLeft class="float-left w-4 md:float-right" />
      </button>
      <div class="text-black border-box inline-block mt-3 w-1/5 text-center font-bold">{{ activeSlide + 1 }}/{{ amendedEntries.length }}</div>
      <button
        class="border-box float-right mt-4 w-2/5 cursor-pointer focus:ring-1 focus:ring-white focus:ring-offset-1 focus:rounded-md hover:text-app-green text-black transform transition duration-200"
        :aria-label="t_('next')"
        @click="slider.glide.go('>')"
      >
        <AtomIconArrowRight class="float-right w-4 md:float-left" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import get from 'lodash/get'
import { getMediaImage } from '~/composables/media'
import { setupTranslationPrefixer } from '~/composables/util'

//
// For vue-glide documentation, see https://antonreshetov.github.io/vue-glide/props/
//

interface Props {
  addShadow?: boolean
  media?: Array<any>
  entries?: Array<any>
  images?: Array<any>
}

const props = withDefaults(defineProps<Props>(), {
  addShadow: true,
  media: () => [],
  entries: () => [],
  images: () => [],
})

const slider = ref()
const activeSlide = ref(0)

const amendedEntries = computed(() =>
  [
    ...props.media.map((media) => {
      switch (media.type) {
        case 'image':
          return { type: 'image', image: getMediaImage(media) }
        case 'youtube':
          return { type: 'youtube', youtube: media }
        case 'entry':
          return { type: 'entry', subtitle: media.subtitle, introduction: media.introduction, image: getMediaImage(media.image) }
        default:
          return undefined
      }
    }),
    ...props.entries.map((entry) => ({
      type: 'entry',
      subtitle: entry.subtitle,
      introduction: entry.introduction,
      image: getMediaImage(entry.image),
    })),
    ...props.images.map((image) => ({ type: 'image', image: getMediaImage(image) })),
  ].filter((entry) => !!entry),
)

const { t_ } = setupTranslationPrefixer('carousel')
</script>

<style lang="scss">
@import '@glidejs/glide/dist/css/glide.core';
@import '@glidejs/glide/dist/css/glide.theme';

.youtube iframe {
  @apply w-full h-full;
}
</style>

<style lang="scss" scoped>
.slider {
  ul {
    li {
      display: inline-block;
    }
  }
}

.shadowed {
  @apply relative block pr-6;
  z-index: 20;

  &::after {
    content: ' ';
    z-index: -1;
    @apply bg-app-green-200 rounded-xl h-full absolute top-0 left-0 bottom-0 right-0 mt-6 ml-6;
  }
}
</style>
